import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import {
  fetchTeacherSubjects,
  fetchSubjects,
  fetchClassStudents,
  fetchGrades,
  fetchAssignments,
  createGrade,
  updateGrade,
  publishAssignmentGrades,
  submitGradesToAdmin,
  recallGradeSubmission,
  type TeacherSubjectDto,
  type SubjectDto,
  type UserDto,
  type GradeDto,
  type AssignmentDto,
  type CreateGradePayload,
} from '../../api/axios';
import { ClassSelector } from './ClassSelector';
import { GradingTabs, GradingTab } from './GradingTabs';
import { GradeEntryTable } from './GradeEntryTable';
import { GradeActions } from './GradeActions';
import { getGrade } from '../../utils/msceGrading';
import { gradeCodeFromMarks } from '../../utils/letterGrading';
import type { SchoolCode } from '../../types';
import {
  formatGradingTerm,
  gradingTermSelectOptions,
  defaultGradingTermYears,
} from '../../utils/gradingTerm';
import { formatClassLevelLabel } from '../../utils/classLevelLabel';
import { fetchSchoolSettings } from '../../api/axios';


export const TeacherGradingSection: React.FC = () => {
  const { user } = useAuth();
  const { currentSchool, userSchool, getSchoolInfo } = useSchool();
  const schoolInfo = getSchoolInfo(currentSchool);
  const teacherSchool = userSchool ?? currentSchool;
  
  const [teacherSubjects, setTeacherSubjects] = useState<TeacherSubjectDto[]>([]);
  const [allSubjects, setAllSubjects] = useState<SubjectDto[]>([]);
  const [selectedForm, setSelectedForm] = useState<number | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<GradingTab>('exam');
  const [currentTerm, setCurrentTerm] = useState(() =>
    formatGradingTerm(1, new Date().getFullYear())
  );

  useEffect(() => {
    fetchSchoolSettings(currentSchool).then(s => {
      setCurrentTerm(formatGradingTerm(s.current_term, Number(s.current_academic_year)));
    }).catch(() => {});
  }, [currentSchool]);
  const [students, setStudents] = useState<UserDto[]>([]);
  const [grades, setGrades] = useState<GradeDto[]>([]);
  const [assignments, setAssignments] = useState<AssignmentDto[]>([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [gradeValues, setGradeValues] = useState<Record<string, { marks: string; grade: string }>>({});
  
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const calculateGrade = (marks: number): string => {
    if (selectedForm == null) return String(getGrade(marks));
    return gradeCodeFromMarks(currentSchool as SchoolCode, selectedForm, marks);
  };

  // Load teacher subjects and all subjects on mount
  useEffect(() => {
    if (user?.id) {
      loadTeacherSubjects();
      loadAllSubjects();
    }
  }, [user?.id, teacherSchool]);

  // Load students and grades when form/subject changes
  useEffect(() => {
    if (selectedForm && selectedSubjectId) {
      loadStudents();
      loadGrades();
    }
  }, [selectedForm, selectedSubjectId, currentTerm, currentSchool]);

  // Load assignments when form or subject changes
  useEffect(() => {
    if (selectedForm && selectedSubjectId) {
      loadAssignments();
    }
  }, [selectedForm, selectedSubjectId, currentSchool]);

  const loadTeacherSubjects = async () => {
    if (!user?.id) {
      console.error('No user ID available');
      return;
    }

    setLoading(true);
    try {
      // Pass teacher_id and school to only get THIS teacher's assignments for THIS school
      const data = await fetchTeacherSubjects(undefined, user.id, teacherSchool);
      setTeacherSubjects(data);
    } catch (error) {
      console.error('Failed to load teacher subjects', error);
      alert('Failed to load your assigned classes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAllSubjects = async () => {
    try {
      const data = await fetchSubjects({ school: teacherSchool });
      setAllSubjects(data);
    } catch (error) {
      console.error('Failed to load all subjects', error);
    }
  };

  const loadStudents = async () => {
    if (!selectedForm || !selectedSubjectId) return;

    setLoadingStudents(true);
    try {
      const data = await fetchClassStudents(selectedForm, selectedSubjectId, currentSchool);
      setStudents(data);
    } catch (error) {
      console.error('Failed to load students', error);
      alert('Failed to load students. Please try again.');
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadGrades = async () => {
    if (!selectedForm || !selectedSubjectId) return;

    setLoadingGrades(true);
    try {
      const data = await fetchGrades(selectedForm, undefined, currentTerm, undefined, selectedSubjectId, currentSchool);
      setGrades(data);
    } catch (error) {
      console.error('Failed to load grades', error);
    } finally {
      setLoadingGrades(false);
    }
  };

  const loadAssignments = async () => {
    if (!selectedForm || !selectedSubjectId) return;

    try {
      const data = await fetchAssignments({ school: currentSchool });
      
      // Get subject name from teacher subjects
      const subject = teacherSubjects.find((ts) => ts.subject_id === selectedSubjectId);
      const subjectName = subject?.subject?.name;
      
      // Filter assignments by BOTH subject AND form
      const filtered = data.filter((a) => {
        const matchesSubject = a.subject.toUpperCase() === subjectName?.toUpperCase();
        const matchesForm = a.class_name?.toLowerCase().includes(`form ${selectedForm}`.toLowerCase());
        return matchesSubject && matchesForm;
      });
      
      setAssignments(filtered);
      
      // Auto-select first assignment if available
      if (filtered.length > 0 && !selectedAssignmentId) {
        setSelectedAssignmentId(filtered[0].id);
      }
    } catch (error) {
      console.error('Failed to load assignments', error);
    }
  };

  const handleGradeChange = (studentId: number, marks: string) => {
    const marksNum = parseInt(marks, 10);
    const grade = isNaN(marksNum) ? '' : calculateGrade(marksNum);

    setGradeValues((prev) => ({
      ...prev,
      [`${studentId}`]: { marks, grade },
    }));
  };

  const handleSaveGrades = async () => {
    if (!selectedSubjectId) return;

    try {
      const entries = Object.entries(gradeValues);
      if (entries.length === 0) {
        alert('⚠️ No grades entered!\n\nPlease enter marks for at least one student before saving.');
        return;
      }

      let savedCount = 0;
      const invalidEntries: string[] = [];
      const savedStudentIds = new Set<string>();
      for (const [studentIdStr, value] of entries) {
        const studentId = parseInt(studentIdStr, 10);
        const marks = parseInt(value.marks, 10);

        if (isNaN(marks) || marks < 0 || marks > 100) {
          invalidEntries.push(studentIdStr);
          continue;
        }

        const isExamGrade = activeTab === 'exam';
        const assignmentId = isExamGrade ? null : selectedAssignmentId;

        // Check if grade already exists
        const existingGrade = grades.find((g) => {
          if (isExamGrade) {
            return (
              g.student_id === studentId &&
              g.subject_id === selectedSubjectId &&
              g.term === currentTerm &&
              (g.assignment_id === null || g.assignment_id === undefined)
            );
          } else {
            return g.student_id === studentId && g.assignment_id === assignmentId;
          }
        });

        const gradePayload: CreateGradePayload = {
          student_id: studentId,
          subject_id: selectedSubjectId,
          assignment_id: assignmentId,
          marks,
          grade: value.grade,
          position: 1,
          teacher_remarks: null,
          term: currentTerm,
          published: false,
        };

        if (existingGrade) {
          await updateGrade(existingGrade.id, gradePayload);
        } else {
          await createGrade(gradePayload);
        }
        savedCount++;
        savedStudentIds.add(studentIdStr);
      }

      await loadGrades();
      setGradeValues((prev) => {
        const next = { ...prev };
        for (const studentId of savedStudentIds) {
          delete next[studentId];
        }
        return next;
      });

      if (invalidEntries.length > 0) {
        alert(`⚠️ ${invalidEntries.length} entry/entries were skipped because marks were invalid (must be 0-100).`);
      }

      if (activeTab === 'exam') {
        alert(
          `✓ Exam grades saved successfully!\n\n${savedCount} grade(s) saved.\n\nNext: Click "SUBMIT TO ADMIN" to send these grades for review.`
        );
      } else {
        alert(`✓ Assignment grades saved successfully!\n\n${savedCount} grade(s) saved.`);
      }
    } catch (error) {
      console.error('Failed to save grades', error);
      const data = (error as any)?.response?.data;
      const firstValidationError =
        data?.errors && typeof data.errors === 'object'
          ? (Object.values(data.errors).flat()[0] as string | undefined)
          : undefined;
      const message =
        data?.error || data?.message || firstValidationError || (error as any)?.message || 'Unknown error';
      alert('❌ Failed to save grades.\n\nError: ' + message);
    }
  };

  const handleSubmitToAdmin = async () => {
    if (!selectedForm || !selectedSubjectId) return;

    const examGrades = grades.filter(
      (g) =>
        g.subject_id === selectedSubjectId &&
        g.term === currentTerm &&
        (g.assignment_id === null || g.assignment_id === undefined)
    );

    if (examGrades.length === 0) {
      alert('⚠️ No exam grades to submit!\n\nPlease save exam grades first.');
      return;
    }

    const allSubmitted = examGrades.every((g) => !!(g as any).submitted_to_admin);
    const allPublished = examGrades.every((g) => !!(g as any).admin_published);

    if (allPublished) {
      alert('✓ These grades are already published!\n\nStudents can see them on their report cards.');
      return;
    }

    if (allSubmitted) {
      alert('✓ These grades are already submitted!\n\nWait for the admin to review and publish them.');
      return;
    }

    if (
      !window.confirm(
        `Submit ${examGrades.length} exam grade(s) to the admin for review?\n\nOnce submitted, the admin will review and publish them to students.`
      )
    ) {
      return;
    }

    try {
      const response = await submitGradesToAdmin({
        subject_id: selectedSubjectId,
        form: selectedForm,
        term: currentTerm,
        school: currentSchool,
      });

      await loadGrades();

      const subject = teacherSubjects.find((ts) => ts.subject_id === selectedSubjectId);
      alert(
        `✓ Exam grades submitted successfully!\n\nSubject: ${subject?.subject?.name || 'Subject'}\nForm: ${selectedForm}\nGrades: ${response.updated_count}\n\nThe admin must use "Publish to students" in Grade Review before these appear on report cards and in student exam results.`
      );
    } catch (error) {
      console.error('Failed to submit grades', error);
      alert('❌ Failed to submit grades.\n\nError: ' + ((error as any)?.message || 'Unknown error'));
    }
  };

  const handlePublishAssignment = async () => {
    if (!selectedAssignmentId) return;

    const assignmentGrades = grades.filter((g) => g.assignment_id === selectedAssignmentId);

    if (assignmentGrades.length === 0) {
      alert('⚠️ No grades to publish!\n\nPlease save grades first.');
      return;
    }

    if (
      !window.confirm(
        `Publish ${assignmentGrades.length} assignment grade(s) to students?\n\nStudents will be able to see their grades immediately.`
      )
    ) {
      return;
    }

    try {
      await publishAssignmentGrades(selectedAssignmentId);
      await loadGrades();

      const assignment = assignments.find((a) => a.id === selectedAssignmentId);
      alert(
        `✓ Assignment grades published successfully!\n\nAssignment: ${assignment?.title || 'Assignment'}\nGrades: ${assignmentGrades.length}\n\nStudents can now see their grades.`
      );
    } catch (error) {
      console.error('Failed to publish grades', error);
      alert('❌ Failed to publish grades.\n\nError: ' + ((error as any)?.message || 'Unknown error'));
    }
  };

  const handleRecallSubmission = async () => {
    if (!selectedForm || !selectedSubjectId) return;

    const examGrades = grades.filter(
      (g) =>
        g.subject_id === selectedSubjectId &&
        g.term === currentTerm &&
        (g.assignment_id === null || g.assignment_id === undefined) &&
        !!(g as any).submitted_to_admin
    );

    if (examGrades.length === 0) {
      alert('⚠️ No submitted exam grades to recall for this term.');
      return;
    }

    const subjectName = selectedSubject?.subject?.name || 'Subject';
    if (
      !window.confirm(
        `Recall submission for ${subjectName}, ${formatClassLevelLabel(currentSchool, selectedForm)}, ${currentTerm}?\n\nThis will remove the submission from admin review and also clear any publish-to-students flag.\n\nYou can then correct marks and submit again.`
      )
    ) {
      return;
    }

    try {
      const response = await recallGradeSubmission({
        subject_id: selectedSubjectId,
        form: selectedForm,
        term: currentTerm,
        school: currentSchool,
      });
      await loadGrades();
      alert(
        `✓ Submission recalled.\n\nUpdated: ${response.updated_count} grade(s).\n\nNow edit marks and click SUBMIT TO ADMIN again when ready.`
      );
    } catch (error) {
      console.error('Failed to recall submission', error);
      alert(
        '❌ Failed to recall submission.\n\nError: ' +
          ((error as any)?.response?.data?.message || (error as any)?.message || 'Unknown error')
      );
    }
  };

  // Get submission status for exam grades
  const getExamSubmissionStatus = () => {
    const examGrades = grades.filter(
      (g) =>
        g.subject_id === selectedSubjectId &&
        g.term === currentTerm &&
        (g.assignment_id === null || g.assignment_id === undefined)
    );

    return {
      hasGrades: examGrades.length > 0,
      allSubmitted: examGrades.length > 0 && examGrades.every((g) => !!(g as any).submitted_to_admin),
      allPublished: examGrades.length > 0 && examGrades.every((g) => !!(g as any).admin_published),
      gradeCount: examGrades.length,
    };
  };

  // Get filtered students and grades based on active tab
  const getFilteredData = () => {
    if (activeTab === 'exam') {
      const examGrades = grades.filter(
        (g) =>
          g.subject_id === selectedSubjectId &&
          g.term === currentTerm &&
          (g.assignment_id === null || g.assignment_id === undefined)
      );
      return { students, grades: examGrades };
    } else {
      const assignmentGrades = grades.filter((g) => g.assignment_id === selectedAssignmentId);
      return { students, grades: assignmentGrades };
    }
  };

  const selectedSubject = teacherSubjects.find((ts) => ts.subject_id === selectedSubjectId);
  const filteredData = getFilteredData();
  const submissionStatus = getExamSubmissionStatus();
  const gradingTermOptions = gradingTermSelectOptions(defaultGradingTermYears());

  return (
    <div style={{ padding: '32px 0' }}>
      {/* Header with School Badge */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
          <h2
            style={{
              fontSize: 28,
              fontWeight: 700,
              margin: 0,
              color: '#333',
            }}
          >
            📝 Grading Center
          </h2>
          <div
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={() => setShowHelp(true)}
            onMouseLeave={() => setShowHelp(false)}
          >
            <button
              type="button"
              aria-label="Learn more about Grading Center"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: '1px solid #93c5fd',
                background: '#eff6ff',
                color: '#1d4ed8',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'help',
              }}
            >
              ?
            </button>
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                width: 320,
                maxWidth: '80vw',
                padding: '12px 14px',
                borderRadius: 10,
                border: '1px solid #bfdbfe',
                background: '#fff',
                boxShadow: '0 10px 25px rgba(15,23,42,0.15)',
                color: '#1f2937',
                fontSize: 13,
                zIndex: 30,
                opacity: showHelp ? 1 : 0,
                visibility: showHelp ? 'visible' : 'hidden',
                transform: showHelp ? 'translateY(0)' : 'translateY(-4px)',
                transition: 'opacity 0.16s ease, transform 0.16s ease',
                pointerEvents: 'none',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Select a class and subject before entering marks.</li>
                <li>Save grades first, then submit exam grades to admin for review.</li>
                <li>Use assignment publish for assignment marks; exam publishing is done by admin.</li>
              </ul>
            </div>
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              background: schoolInfo.theme.primaryColor,
              color: '#fff',
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 700,
              boxShadow: `0 2px 8px ${schoolInfo.theme.primaryColor}40`,
            }}
          >
            <img
              src={schoolInfo.theme.logoIcon}
              alt=""
              style={{ width: 20, height: 20, objectFit: 'contain', display: 'block' }}
            />
            <span>{schoolInfo.name}</span>
          </span>
        </div>
        <p style={{ fontSize: 15, color: '#666', margin: 0 }}>
          Enter and manage grades for your classes at {schoolInfo.name}
        </p>
      </div>

      {/* Class Selector */}
      <ClassSelector
        teacherSubjects={teacherSubjects}
        allSubjects={allSubjects}
        selectedForm={selectedForm}
        selectedSubjectId={selectedSubjectId}
        onFormChange={(form) => {
          setSelectedForm(form);
          setSelectedSubjectId(null);
          setGradeValues({});
        }}
        onSubjectChange={(subjectId) => {
          setSelectedSubjectId(subjectId);
          setGradeValues({});
        }}
        loading={loading}
        school={teacherSchool}
      />

      {/* Grading Interface */}
      {selectedForm && selectedSubjectId && (
        <>
          {/* Class Info Header */}
          <div
            style={{
              background: '#fff',
              padding: 20,
              borderRadius: 8,
              border: '1px solid #e0e0e0',
              marginBottom: 24,
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#333', marginBottom: 4 }}>
                  {formatClassLevelLabel(currentSchool, selectedForm)} — {selectedSubject?.subject?.name}
                </div>
                <div style={{ fontSize: 14, color: '#666' }}>
                  {students.length} {students.length === 1 ? 'Student' : 'Students'}
                </div>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#666',
                    marginBottom: 4,
                    display: 'block',
                  }}
                >
                  TERM
                </label>
                <select
                  value={currentTerm}
                  onChange={(e) => setCurrentTerm(e.target.value)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: '2px solid #e0e0e0',
                    background: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {gradingTermOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Grading Tabs */}
          <GradingTabs
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setGradeValues({});
            }}
            examCount={filteredData.grades.filter((g) => !g.assignment_id).length}
            assignmentCount={assignments.length}
          />

          {/* Assignment Selector (for assignments tab) */}
          {activeTab === 'assignments' && assignments.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#666',
                  marginBottom: 8,
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Select Assignment
              </label>
              <select
                value={selectedAssignmentId || ''}
                onChange={(e) => {
                  setSelectedAssignmentId(Number(e.target.value));
                  setGradeValues({});
                }}
                style={{
                  width: '100%',
                  maxWidth: 500,
                  padding: '12px 16px',
                  borderRadius: 6,
                  border: '2px solid #e0e0e0',
                  background: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {assignments.map((assignment) => (
                  <option key={assignment.id} value={assignment.id}>
                    {assignment.title} - Due: {new Date(assignment.due_date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* No Assignments Message */}
          {activeTab === 'assignments' && assignments.length === 0 && (
            <div
              style={{
                padding: 60,
                textAlign: 'center',
                background: '#fff',
                borderRadius: 12,
                border: '2px dashed #e0e0e0',
              }}
            >
              <div style={{ fontSize: 64, marginBottom: 16 }}>📚</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                No Assignments Created
              </div>
              <div style={{ fontSize: 14, color: '#999' }}>
                Create assignments from the Assignments section to grade them here.
              </div>
            </div>
          )}

          {/* Grade Entry Table */}
          {(activeTab === 'exam' || (activeTab === 'assignments' && selectedAssignmentId)) && (
            <>
              <GradeEntryTable
                students={filteredData.students}
                grades={filteredData.grades}
                gradeValues={gradeValues}
                onGradeChange={handleGradeChange}
                assignmentId={activeTab === 'assignments' ? selectedAssignmentId : null}
                loading={loadingStudents || loadingGrades}
                school={currentSchool as SchoolCode}
                form={selectedForm!}
              />

              {/* Grade Actions */}
              <GradeActions
                onSave={handleSaveGrades}
                onSubmitToAdmin={activeTab === 'exam' ? handleSubmitToAdmin : undefined}
                onPublish={activeTab === 'assignments' ? handlePublishAssignment : undefined}
                saveDisabled={Object.keys(gradeValues).length === 0}
                submissionStatus={activeTab === 'exam' ? submissionStatus : undefined}
                isExamGrades={activeTab === 'exam'}
              />

              {/* Recall submission (exam grades only) */}
              {activeTab === 'exam' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={handleRecallSubmission}
                    disabled={!submissionStatus.allSubmitted}
                    style={{
                      padding: '10px 20px',
                      background: submissionStatus.allSubmitted ? '#b45309' : '#ccc',
                      color: '#fff',
                      borderRadius: 6,
                      border: 'none',
                      cursor: submissionStatus.allSubmitted ? 'pointer' : 'not-allowed',
                      fontWeight: 800,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      opacity: submissionStatus.allSubmitted ? 1 : 0.6,
                      boxShadow: submissionStatus.allSubmitted ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                    }}
                    title={
                      submissionStatus.allSubmitted
                        ? 'Recall submission from admin review'
                        : 'Only available after submission'
                    }
                  >
                    ↩ Recall Submission
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
